import { toSquareWebp } from "./avatars.js";
import type { AuctionCurrency } from "./currency.js";
import { DemoError, demoTournaments } from "./demo.js";
import { SPORT } from "./sport.js";
import { supabase } from "./supabase.js";
import type {
  EntryContacts,
  EventCategory,
  EventMvp,
  EventEntry,
  EventFormat,
  EventOrdering,
  TiePairing,
  Kind,
  PairingStrategy,
  RegistrationStatus,
  Tournament,
  TournamentEvent,
} from "./types.js";

const TOURNAMENT_COLUMNS =
  "id, name, starts_on, ends_on, venue, description, created_by, created_at, contact_required, auction, private, logo_url, auction_started_at, auction_currency, finished_at";
const EVENT_COLUMNS =
  "id, tournament_id, name, sport, kind, format, category, matches_per_player, tie_pairing, ordering, courts, min_rating, max_rating, capacity, entries_close_at, pairing, counts_for_rating, description, seeding_closed_at, lots_published_at, squad_fill, auction_finished_at, created_at";
/**
 * Entries ride along with their event rather than being fetched by id afterwards. One round
 * trip, and — the reason that matters — no `event_id=in.(...)` list to outgrow the 8KB URL
 * PostgREST will accept.
 *
 * This reads the `registrations` table, not the `event_registrations` view, because a view
 * has no foreign key for PostgREST to embed through. The cost is that `waitlist_position`
 * does not come with it; `waitlistPosition` in types.ts derives the same ordering, and says
 * there that the SQL is the authority.
 */
const ENTRY_COLUMNS = "member_id, status, requested_at, seed, members (display_name, avatar_url)";

interface EntryRow {
  member_id: string;
  status: RegistrationStatus;
  requested_at: string;
  seed: number | null;
  members: { display_name: string; avatar_url: string | null } | null;
}

interface EventRow {
  id: string;
  tournament_id: string;
  name: string;
  sport: string;
  kind: Kind;
  format: EventFormat;
  category: EventCategory;
  matches_per_player: number | null;
  tie_pairing: TiePairing;
  ordering: EventOrdering;
  courts: number;
  min_rating: number | null;
  max_rating: number | null;
  capacity: number | null;
  entries_close_at: string | null;
  pairing: PairingStrategy;
  counts_for_rating: boolean;
  description: string | null;
  seeding_closed_at: string | null;
  lots_published_at: string | null;
  squad_fill: TournamentEvent["squadFill"];
  auction_finished_at: string | null;
  created_at: string;
  registrations: EntryRow[];
}

export interface TournamentRow {
  id: string;
  name: string;
  starts_on: string;
  ends_on: string | null;
  venue: string | null;
  description: string | null;
  created_by: string | null;
  created_at: string;
  contact_required: boolean;
  auction: boolean;
  private: boolean;
  logo_url: string | null;
  auction_started_at: string | null;
  auction_currency: AuctionCurrency;
  finished_at: string | null;
  events: EventRow[];
}

function toEntry(row: EntryRow): EventEntry {
  return {
    memberId: row.member_id,
    displayName: row.members?.display_name ?? "Someone",
    avatarUrl: row.members?.avatar_url ?? null,
    status: row.status,
    requestedAt: row.requested_at,
    seed: row.seed ?? null,
  };
}

function toEvent(row: EventRow): TournamentEvent {
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    name: row.name,
    sport: row.sport as TournamentEvent["sport"],
    kind: row.kind,
    format: row.format,
    category: row.category,
    matchesPerPlayer: row.matches_per_player,
    tiePairing: row.tie_pairing,
    ordering: row.ordering,
    courts: row.courts,
    minRating: row.min_rating,
    maxRating: row.max_rating,
    capacity: row.capacity,
    entriesCloseAt: row.entries_close_at,
    pairing: row.pairing,
    countsForRating: row.counts_for_rating,
    description: row.description,
    seedingClosedAt: row.seeding_closed_at,
    lotsPublishedAt: row.lots_published_at,
    squadFill: row.squad_fill,
    auctionFinishedAt: row.auction_finished_at,
    createdAt: row.created_at,
    // Withdrawn rows stay in the table — rejoining has to know you had left, and who
    // pulled out is worth keeping — but nothing upstream of here wants them.
    entries: (row.registrations ?? []).filter((entry) => entry.status !== "withdrawn").map(toEntry),
  };
}

export function toTournament(row: TournamentRow): Tournament {
  return {
    id: row.id,
    name: row.name,
    startsOn: row.starts_on,
    endsOn: row.ends_on,
    venue: row.venue,
    description: row.description,
    createdBy: row.created_by,
    createdAt: row.created_at,
    contactRequired: row.contact_required,
    auction: row.auction,
    private: row.private,
    logoUrl: row.logo_url,
    auctionStartedAt: row.auction_started_at,
    auctionCurrency: row.auction_currency,
    finishedAt: row.finished_at,
    events: (row.events ?? []).map(toEvent).sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/**
 * Every tournament in this sport, most recent start date first.
 *
 * The tournament carries the sport now (20260906000200). It used not to — only events did,
 * and this stripped the other sports' events and let the tournament itself through, so a
 * table-tennis tournament appeared in the badminton app saying "No events yet." That was
 * meant to keep a tournament shared across sports findable before it had an event in
 * yours. It read as the apps being mixed up, because that is exactly what it looks like.
 *
 * The embedded filter stays alongside the new one. A tournament's events are all its own
 * sport — the database enforces that on write — so it selects nothing extra today. It is
 * here so that a row predating the trigger cannot render under the wrong heading.
 */
export async function fetchTournaments(): Promise<Tournament[]> {
  if (!supabase) return demoTournaments;

  const { data, error } = await supabase
    .from("tournaments")
    .select(`${TOURNAMENT_COLUMNS}, events (${EVENT_COLUMNS}, registrations (${ENTRY_COLUMNS}))`)
    .eq("sport", SPORT)
    .eq("events.sport", SPORT)
    .order("starts_on", { ascending: false });

  if (error) throw new Error(error.message);
  return (data as unknown as TournamentRow[]).map(toTournament);
}

export interface CreateTournamentInput {
  name: string;
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  description: string | null;
  /** Entering needs a flat number and a phone number on file. */
  contactRequired: boolean;
  /** Teams picked by auction. Can't be changed once the tournament exists. */
  auction: boolean;
  /** Visible only to people invited. Can't be changed once the tournament exists. */
  private: boolean;
}

/**
 * Organiser and admin only — `tournaments_write` enforces that itself, so this is refused
 * by the database rather than by the absence of a button for anyone who calls it directly.
 */
export async function createTournament(
  userId: string,
  input: CreateTournamentInput,
): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("tournaments")
    .insert(toTournamentInsert(userId, input))
    .select("id")
    .single();

  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

/** The row `createTournament` writes, apart from the writing, so it can be read and tested. */
export function toTournamentInsert(userId: string, input: CreateTournamentInput) {
  return {
    // Never a field on the form, the same way a match's sport never is: the build says
    // which sport this is, and a tournament created here is this sport's.
    sport: SPORT,
    name: input.name.trim(),
    starts_on: input.startsOn,
    ends_on: input.endsOn || null,
    venue: input.venue || null,
    description: input.description || null,
    contact_required: input.contactRequired,
    auction: input.auction,
    private: input.private,
    created_by: userId,
  };
}

/**
 * Switch "contact details required" on or off. Organisers only: `tournaments_write` refuses
 * anybody else, and refuses by matching no row rather than by raising — so no row back is the
 * refusal, and is reported as one rather than as a switch that quietly did not move.
 *
 * Existing entries are never checked again, either way; the database checks at registration.
 */
export async function setTournamentContactRequired(
  tournamentId: string,
  contactRequired: boolean,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("tournaments")
    .update({ contact_required: contactRequired })
    .eq("id", tournamentId)
    .select("id");

  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error("only an organiser can change this");
}

interface PastEventRow {
  id: string;
  name: string;
  event_mvps: { member_id: string; members: { display_name: string } | null }[];
}

export interface PastTournamentRow extends Omit<TournamentRow, "events"> {
  events: PastEventRow[];
}

/** The MVPs of one event, by name, for a line on the history page. */
export interface EventMvpLine {
  eventName: string;
  names: string[];
}

/** A finished tournament as the history page shows it: the banner's facts, and what was won. */
export interface PastTournament {
  tournament: Tournament;
  eventCount: number;
  mvps: EventMvpLine[];
}

/**
 * One line per event that has an MVP, in event-name order, with the names alphabetical. An
 * event with nobody named is left out, not listed with a gap, and a tournament with none at all
 * yields nothing.
 */
export function mvpsByEvent(events: PastEventRow[]): EventMvpLine[] {
  return events
    .map((event) => ({
      eventName: event.name,
      names: event.event_mvps
        .map((mvp) => mvp.members?.display_name ?? "Someone")
        .sort((a, b) => a.localeCompare(b)),
    }))
    .filter((line) => line.names.length > 0)
    .sort((a, b) => a.eventName.localeCompare(b.eventName));
}

export function toPastTournament(row: PastTournamentRow): PastTournament {
  return {
    tournament: toTournament({ ...row, events: [] }),
    eventCount: row.events.length,
    mvps: mvpsByEvent(row.events),
  };
}

/**
 * Every finished tournament this member may see, newest start first, with each event's MVPs.
 *
 * One request: the events and their MVPs are embedded, each along the one foreign key, so no
 * list of ids goes back into a URL (PostgREST refuses those past about 8KB) and nothing is
 * ambiguous to embed. `tournaments`, `events` and `event_mvps` are each read through the
 * private-tournament policies, so one the caller cannot see is simply not returned.
 */
export async function fetchPastTournaments(): Promise<PastTournament[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tournaments")
    .select(
      `${TOURNAMENT_COLUMNS}, events (id, name, event_mvps (member_id, members (display_name)))`,
    )
    .eq("sport", SPORT)
    .eq("events.sport", SPORT)
    .not("finished_at", "is", null)
    .order("starts_on", { ascending: false });

  if (error) throw new Error(error.message);
  return (data as unknown as PastTournamentRow[]).map(toPastTournament);
}

/**
 * Finish a tournament: scores are final, each event's MVP is named, and it leaves the Events
 * list. Organisers only, and refused with a count while matches are pending or disputed — the
 * database says "3 matches are not yet confirmed" and that is passed through as it is.
 */
export async function finishTournament(tournamentId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("finish_tournament", { target_tournament: tournamentId });
  if (error) throw new Error(error.message);
}

/** The label beside an auction tournament's prices. Organisers only, before the auction starts. */
export async function setAuctionCurrency(
  tournamentId: string,
  currency: AuctionCurrency,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_auction_currency", {
    target_tournament: tournamentId,
    new_currency: currency,
  });
  if (error) throw new Error(error.message);
}

/** Admin only: the way back from finishing by mistake. Clears the MVPs with it. */
export async function reopenTournament(tournamentId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("reopen_tournament", { target_tournament: tournamentId });
  if (error) throw new Error(error.message);
}

/**
 * How many of a tournament's matches stand in the way of finishing it. Counted by the same
 * rule the database applies — pending or disputed, in any of its events — so the button can
 * say so before it is pressed; `finish_tournament` counts again and is the authority.
 *
 * `events!inner` makes the filter on the embedded row a filter on matches, and `head` with
 * `count` returns the number without the rows.
 */
export async function fetchOutstandingMatchCount(tournamentId: string): Promise<number> {
  if (!supabase) return 0;

  const { count, error } = await supabase
    .from("matches")
    .select("id, events!inner (tournament_id)", { count: "exact", head: true })
    .in("status", ["pending", "disputed"])
    .eq("events.tournament_id", tournamentId);

  if (error) throw new Error(error.message);
  return count ?? 0;
}

interface EventMvpRow {
  event_id: string;
  member_id: string;
  wins: number;
  point_diff: number;
  matches_played: number;
  members: { display_name: string; avatar_url: string | null } | null;
}

export function toEventMvp(row: EventMvpRow): EventMvp {
  return {
    eventId: row.event_id,
    memberId: row.member_id,
    displayName: row.members?.display_name ?? "Someone",
    avatarUrl: row.members?.avatar_url ?? null,
    wins: row.wins,
    pointDiff: row.point_diff,
    matchesPlayed: row.matches_played,
  };
}

/** The MVPs of every event in one tournament; empty until it is finished. */
export async function fetchEventMvps(tournamentId: string): Promise<EventMvp[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("event_mvps")
    .select(
      "event_id, member_id, wins, point_diff, matches_played, members (display_name, avatar_url), events!inner (tournament_id)",
    )
    .eq("events.tournament_id", tournamentId);

  if (error) throw new Error(error.message);
  return (data as unknown as EventMvpRow[]).map(toEventMvp);
}

/**
 * Close registration for one event now. Organisers only. It is a call rather than an update of
 * `entries_close_at` from here so the cutoff is the database's clock, not this device's: a slow
 * phone would otherwise leave entries open for as long as it is slow. It replaces any scheduled
 * cutoff date, and is refused if the event is already closed.
 */
export async function closeEventEntries(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("close_event_entries", { target_event: eventId });
  if (error) throw new Error(error.message);
}

/** Reopen a closed event: no cutoff date, registering works. Refused if it is not closed. */
export async function reopenEventEntries(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("reopen_event_entries", { target_event: eventId });
  if (error) throw new Error(error.message);
}

const LOGO_BUCKET = "tournament-logos";

async function setLogoUrl(tournamentId: string, logoUrl: string | null): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("tournaments")
    .update({ logo_url: logoUrl })
    .eq("id", tournamentId)
    .select("id");

  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error("only an organiser can change this");
}

/**
 * Set or replace a tournament's logo. Organisers only: the bucket's policies refuse an
 * upload from anybody else, and `tournaments_write` refuses the row update by matching no
 * row, which `setLogoUrl` reports.
 *
 * The object path is stable (`<id>/logo.webp`, overwritten in place), so the returned URL
 * carries a cache-busting query — otherwise a replaced logo would keep showing the old one
 * until the CDN let go of it.
 */
export async function uploadTournamentLogo(tournamentId: string, file: File): Promise<string> {
  if (!supabase) throw new DemoError();

  if (!file.type.startsWith("image/")) {
    throw new Error("That is not an image.");
  }

  const blob = await toSquareWebp(file, "contain");
  const path = `${tournamentId}/logo.webp`;

  const { error: uploadError } = await supabase.storage
    .from(LOGO_BUCKET)
    .upload(path, blob, { contentType: "image/webp", upsert: true });

  if (uploadError) throw new Error(uploadError.message);

  const {
    data: { publicUrl },
  } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);

  const url = `${publicUrl}?v=${Date.now()}`;
  await setLogoUrl(tournamentId, url);
  return url;
}

/**
 * Remove a tournament's logo. The row is cleared first, so a refused request leaves the
 * file alone; the object is then deleted so a removed logo does not linger in the bucket.
 */
export async function clearTournamentLogo(tournamentId: string): Promise<void> {
  await setLogoUrl(tournamentId, null);

  const { error } = await supabase!.storage.from(LOGO_BUCKET).remove([`${tournamentId}/logo.webp`]);
  if (error) throw new Error(error.message);
}

export interface CreateEventInput {
  tournamentId: string;
  name: string;
  kind: Kind;
  format: EventFormat;
  /** Who may enter. The database fixes it once anybody has registered. */
  category: EventCategory;
  matchesPerPlayer: number | null;
  ordering: EventOrdering;
  minRating: number | null;
  maxRating: number | null;
  capacity: number | null;
  /** ISO, or null for an event that never closes. */
  entriesCloseAt: string | null;
  pairing: PairingStrategy;
  /** Off for a kids' event. Fixed by the database once the event has a match. */
  countsForRating: boolean;
  /** Free text for players. Blank is sent as null; the table refuses anything untrimmed. */
  description: string | null;
}

/**
 * `sport` is never a field here — it is always `SPORT`, the same way a logged match's sport
 * always is. There is nothing for the organiser to pick.
 *
 * `courts` is absent for a different reason: the clubhouse has one court, so the column's
 * default of 1 is the answer every time and asking was a field with one legal value. The
 * column stays, because "how many courts run at once" is real for a club that grows a
 * second one — restoring it means restoring an input, not a migration.
 */
export async function createEvent(input: CreateEventInput): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("events")
    .insert({
      tournament_id: input.tournamentId,
      name: input.name.trim(),
      sport: SPORT,
      kind: input.kind,
      format: input.format,
      category: input.category,
      matches_per_player: input.matchesPerPlayer,
      ordering: input.ordering,
      min_rating: input.minRating,
      max_rating: input.maxRating,
      capacity: input.capacity,
      entries_close_at: input.entriesCloseAt,
      pairing: input.pairing,
      counts_for_rating: input.countsForRating,
      description: cleanDescription(input.description),
    })
    .select("id")
    .single();

  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

/** Trimmed, or null for nothing — the only two shapes `events_description_shape` accepts. */
function cleanDescription(text: string | null): string | null {
  return text?.trim() || null;
}

export interface EventDetails {
  name: string;
  /** Null is uncapped. */
  capacity: number | null;
  /** ISO instant, or null for no cutoff. */
  entriesCloseAt: string | null;
  minRating: number | null;
  maxRating: number | null;
}

/**
 * The organiser's editor: name, places, when entries close, rating bounds. Category and
 * whether the event counts are not here — the database locks both once they matter.
 *
 * Changing `capacity` moves people: lowered, the most recent entries go to the waitlist;
 * raised or cleared, the longest-waiting come in. That happens in the database, in the same
 * update (20261001000900), and a drawn event refuses to lose a drawn player that way — the
 * refusal's sentence comes back as the error.
 */
export async function updateEventDetails(eventId: string, details: EventDetails): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("events")
    .update({
      name: details.name.trim(),
      capacity: details.capacity,
      entries_close_at: details.entriesCloseAt,
      min_rating: details.minRating,
      max_rating: details.maxRating,
    })
    .eq("id", eventId)
    .select("id");

  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error("only an organiser can change this");
}

/**
 * Write, change or clear an event's description. Organisers only, and at any time: nothing
 * reads it but a person, so unlike the category there is no lock. As with
 * `setTournamentContactRequired`, a refusal is no row back rather than an error.
 */
export async function updateEventDescription(
  eventId: string,
  description: string | null,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("events")
    .update({ description: cleanDescription(description) })
    .eq("id", eventId)
    .select("id");

  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error("only an organiser can change this");
}

/**
 * Take a place, or a place in the queue — the database decides which from the capacity, and
 * refuses outright if the event has a rating band this member is outside of.
 *
 * The thrown message is the one Postgres raised, and it is written to be read by a player
 * ("your doubles rating of 2.500 is below this event's minimum of 3.000") rather than
 * translated here. One place to change the wording, and it cannot drift from the rule.
 *
 * The note rides along with the registration rather than following it, so the organiser has
 * "only mornings" from the moment the name appears. Re-registering when you are already in
 * does not touch it — `updateRegistrationNote` is the way to change what you said.
 */
export async function registerForEvent(
  eventId: string,
  note?: string | null,
  onBehalfOf?: string | null,
): Promise<RegistrationStatus> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("register_for_event", {
    target_event: eventId,
    entry_note: note ?? null,
    // A child you look after. The database checks you do, then applies every rule it would
    // apply to them — see 20260929000100.
    on_behalf_of: onBehalfOf ?? null,
  });
  if (error) throw new Error(error.message);
  return data as RegistrationStatus;
}

/**
 * Rewrite your note — or a child's you look after — or clear it with null. False when there
 * is no live entry to note.
 */
export async function updateRegistrationNote(
  eventId: string,
  note: string | null,
  onBehalfOf?: string | null,
): Promise<boolean> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("update_registration_note", {
    target_event: eventId,
    entry_note: note,
    on_behalf_of: onBehalfOf ?? null,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/**
 * Notes on this event's entries, keyed by member.
 *
 * An RPC rather than a column on the entry list, and that is the whole privacy model: a note
 * is free text somebody typed about their week, `authenticated` has no select privilege on
 * the column, and this function returns your own plus — if you are running the event —
 * everybody's. A player therefore gets a map with at most one key in it.
 */
export async function fetchEventNotes(eventId: string): Promise<Map<string, string>> {
  if (!supabase) return new Map();

  const { data, error } = await supabase.rpc("event_notes", { target_event: eventId });
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as { member_id: string; note: string }[];
  return new Map(rows.map((row) => [row.member_id, row.note]));
}

interface EventContactRow {
  member_id: string;
  guardian_name: string | null;
  flat_no: string | null;
  phone: string | null;
  missing: boolean;
}

/**
 * How to reach each entrant, keyed by member — the organiser's, and only theirs.
 *
 * An RPC for the reasons `event_notes` is one: one call per event, no id list to outgrow the
 * URL limit, and the child-to-guardian join done in the database. `event_contacts` answers an
 * organiser or admin and returns nothing to anybody else, so a player gets an empty map.
 *
 * A row with neither detail is how the database says "nobody's to show" while still carrying
 * `missing`, so it becomes an empty list rather than a blank contact.
 */
export async function fetchEventContacts(eventId: string): Promise<Map<string, EntryContacts>> {
  if (!supabase) return new Map();

  const { data, error } = await supabase.rpc("event_contacts", { target_event: eventId });
  if (error) throw new Error(error.message);

  const byMember = new Map<string, EntryContacts>();
  for (const row of (data ?? []) as EventContactRow[]) {
    const entry = byMember.get(row.member_id) ?? { contacts: [], missing: row.missing };
    if (row.flat_no !== null || row.phone !== null) {
      entry.contacts.push({
        guardianName: row.guardian_name,
        flatNo: row.flat_no,
        phone: row.phone,
      });
    }
    byMember.set(row.member_id, entry);
  }
  return byMember;
}

/**
 * Find one event, and the tournament it belongs to, in an already-fetched list.
 *
 * The event page reads through `fetchTournaments` rather than fetching one event by id. That
 * is one cached query for the whole feature instead of two shapes of the same data that can
 * disagree, it keeps the demo fixtures working with no second code path, and at club scale
 * the list is a handful of rows. A shared link on a cold browser therefore costs one request
 * that was going to be made the moment they tapped Events anyway.
 */
export function findEvent(
  tournaments: Tournament[],
  eventId: string,
): { tournament: Tournament; event: TournamentEvent } | null {
  for (const tournament of tournaments) {
    const event = tournament.events.find((candidate) => candidate.id === eventId);
    if (event) return { tournament, event };
  }
  return null;
}

/**
 * Take yourself — or a child you look after — out. Returns false when there was nothing to
 * withdraw, which is not an error.
 */
export async function withdrawRegistration(
  eventId: string,
  onBehalfOf?: string | null,
): Promise<boolean> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("withdraw_registration", {
    target_event: eventId,
    on_behalf_of: onBehalfOf ?? null,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/**
 * Take somebody else out — organiser only, and the database says so.
 *
 * Marks the entry withdrawn rather than deleting it, so it reads to everything downstream
 * exactly like the player pulling out themselves: the longest-waiting entrant is promoted,
 * the draw notices a team sheet has gone stale, and rejoining knows you had left. Unlike a
 * self-withdrawal it lands in the audit trail, because it is an organiser acting on somebody
 * else's evening.
 *
 * False when there was nothing to remove.
 */
/**
 * Put somebody in, as the organiser.
 *
 * The way through the cutoff, and the way the half of the club who tell the organiser
 * rather than tapping anything get onto the list. `add_registration` ignores the cutoff and
 * the division's rating band — §5.3 says the organiser may always move anyone — and records
 * both in the audit trail when it does.
 *
 * Capacity is not ignored: somebody added to a full event waits like anybody else.
 */
export async function addRegistration(
  eventId: string,
  memberId: string,
  note?: string,
): Promise<RegistrationStatus> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("add_registration", {
    target_event: eventId,
    target_member: memberId,
    entry_note: note?.trim() ? note.trim() : null,
  });

  if (error) throw new Error(error.message);
  return data as RegistrationStatus;
}

export async function removeRegistration(eventId: string, memberId: string): Promise<boolean> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("remove_registration", {
    target_event: eventId,
    target_member: memberId,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/** One registered entrant on an event's Payments page. */
export interface EntryPayment {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  paid: boolean;
}

interface EventPaymentRow {
  member_id: string;
  display_name: string;
  avatar_url: string | null;
  paid: boolean;
}

/**
 * Who is registered and whether they have paid. `event_payments` answers an organiser and
 * nobody else, so for anyone else this is an empty list rather than an error.
 */
export async function fetchEventPayments(eventId: string): Promise<EntryPayment[]> {
  if (!supabase) return [];

  const { data, error } = await supabase.rpc("event_payments", { target_event: eventId });
  if (error) throw new Error(error.message);

  return ((data ?? []) as EventPaymentRow[]).map((row) => ({
    memberId: row.member_id,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    paid: row.paid,
  }));
}

export async function setRegistrationPaid(
  eventId: string,
  memberId: string,
  paid: boolean,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_registration_paid", {
    target_event: eventId,
    target_member: memberId,
    is_paid: paid,
  });
  if (error) throw new Error(error.message);
}

/**
 * Save an event's seed order: `memberIds` become seeds 1..n, best first, and every other entry
 * is unseeded. The whole order in one call, so a swap is one write and one audit row; the
 * database refuses it once the draw has results.
 */
export async function setEventSeeds(eventId: string, memberIds: string[]): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_event_seeds", {
    target_event: eventId,
    ordered_members: memberIds,
  });
  if (error) throw new Error(error.message);
}

/**
 * This discipline's ratings for a set of members, keyed by member.
 *
 * Read straight from `ratings`, which every active member may — the board is public within
 * the club and this is the same number. Only the ids asked for, so the request stays small
 * enough to be nowhere near PostgREST's 8KB URL ceiling at club sizes; a tie is a dozen
 * people, not a thousand.
 *
 * Absent from the map means unrated in this discipline, which is a real state and not a
 * missing lookup: it is what the draw seeds last and what the team split spreads by headcount.
 */
export async function fetchRatings(memberIds: string[], kind: Kind): Promise<Map<string, number>> {
  if (!supabase || memberIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("ratings")
    .select("member_id, r")
    .eq("sport", SPORT)
    .eq("kind", kind)
    .in("member_id", memberIds);

  if (error) throw new Error(error.message);
  return new Map((data ?? []).map((row) => [row.member_id as string, row.r as number]));
}
